import { useApolloClient } from '@apollo/client';
import type { FC } from 'react';
import { memo, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Link } from '../../../../../front/src/components/Link';
import { useAppSelector } from '../../../../../front/src/redux/hooks';
import { selectIsSignedIn } from '../../../../../front/src/redux/slices/utils-slice';
import { circleTopicsQuery } from '../../../../../front/src/thread/api/topic';
import { TopicSubject } from '../../../../../front/src/thread/Topic/TopicSubject';
import type { Topic } from '../../../../../front/src/types/api';
import type { ReadersCircleBlockData } from '../../../../../front/src/types/layout';
import { ArticleLink, ArticleMeta, pressMessages, SectionHead } from './pressShared';

interface Props {
  block: ReadersCircleBlockData;
  className?: string;
}

const DEFAULT_LIMIT = 6;

/**
 * "Le cercle des lecteurs" — access block to the website's default readers circle.
 * The default circle is resolved server-side (block.circle), but its topics are
 * member-private, so they are fetched CLIENT-SIDE with the user's session (like
 * BlockLastTopics): a member sees the latest circle topics, everyone else just sees
 * the access CTA. Media mode only (gated in Block.tsx).
 */
export const BlockReadersCircle: FC<Props> = memo(function BlockReadersCircle({ block }) {
  const limit = block.options?.limit ? parseInt(block.options.limit, 10) : DEFAULT_LIMIT;
  const circle = block.circle;
  const circleId = circle?.id;

  const client = useApolloClient();
  const isSignedIn = useAppSelector(selectIsSignedIn);
  const [topics, setTopics] = useState<Topic[]>(block.data || []);

  const fetchCircleTopics = useCallback(async () => {
    if (!circleId) return;
    try {
      const { data } = await client.query({
        query: circleTopicsQuery,
        variables: { groupId: circleId, limit },
        fetchPolicy: 'network-only',
      });
      setTopics((data?.website?.topicList?.topics || []) as Topic[]);
    } catch {
      // Non-members get "permission denied" on circle topics: keep the access CTA only.
      setTopics([]);
    }
  }, [client, circleId, limit]);

  // Circle topics are member-private → fetch them client-side once the user is signed in.
  useEffect(() => {
    if (isSignedIn && circleId) fetchCircleTopics();
  }, [isSignedIn, circleId, fetchCircleTopics]);

  // No default readers circle configured for this website: nothing to show.
  if (!circleId) return null;

  const title = block.options?.customTitle || circle?.name || (
    <FormattedMessage {...pressMessages.readersCircle} />
  );

  return (
    <section className="BlockReadersCircle max-w-5xl mx-auto px-7 pt-16">
      <SectionHead title={title} />
      {!!topics.length && (
        <ul className="flex flex-col border-t-2 border-gray-darkv4">
          {topics.slice(0, limit).map(topic => (
            <li key={topic.id}>
              <ArticleLink
                slug={topic.slug}
                className="group flex flex-col gap-1.5 py-4 border-b border-black/10"
              >
                <h3 className="text-[17px] leading-[1.25] font-bold group-hover:text-primary-base">
                  <TopicSubject subject={topic.subject as string} />
                </h3>
                <ArticleMeta
                  author={topic.author}
                  date={topic.createdAt}
                  reactions={topic.messagesCount}
                />
              </ArticleLink>
            </li>
          ))}
        </ul>
      )}
      {circle?.slug && (
        <div className="mt-6">
          <Link to="group" params={{ slug: circle.slug }} passHref>
            <a className="inline-flex items-center justify-center font-bold rounded-md bg-primary-base text-white px-5 py-3 transition-colors hover:bg-primary-base/90">
              <FormattedMessage {...pressMessages.circleCta} />
            </a>
          </Link>
        </div>
      )}
    </section>
  );
});
